import { Link, router, usePage } from "@inertiajs/react";
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Clock,
  DollarSign,
  Gavel,
  Loader2,
  MessageSquareText,
  Receipt,
  ShoppingCart,
  User,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from "@/components/ui/item";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TextareaWithCounter } from "@/components/ui/textarea-with-counter";
import { GROUPS } from "@/constants/groups";
import { formatCurrencyWithSymbol } from "@/helpers/currency-formatter";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import type {
  ApprovalSnapshotLevel,
  ApprovalSnapshotLevelAssignee,
  PurchaseRequestApprovalDecision,
  PurchaseRequestApprovalModuleHistory,
  PurchaseRequestApprovalState,
  SharedData,
} from "@/types";
import { formatDateTimeAR } from "@/utils/date";

interface PurchaseRequestApprovalPanelProps {
  approval: PurchaseRequestApprovalState;
  purchaseRequestId: number;
  sectorId?: number | null;
  sectorName?: string | null;
}

// ============================================================================
// CONSTANTS & TYPES
// ============================================================================

const DECISION_LABEL: Record<
  PurchaseRequestApprovalDecision["decision"],
  string
> = {
  approved: "Aprobado",
  rejected: "Rechazado",
};

const LEVEL_STATUS_META = {
  pending: {
    label: "Pendiente",
    icon: Clock,
  },
  approved: {
    label: "Aprobado",
    icon: Check,
  },
  rejected: {
    label: "Rechazado",
    icon: X,
  },
};

type LevelStatus = keyof typeof LEVEL_STATUS_META;

interface DialogState {
  open: boolean;
  levelIndex: number | null;
  comment: string;
}

interface OverrideDialogState extends DialogState {
  decision: PurchaseRequestApprovalDecision["decision"];
}

interface StartDialogState {
  open: boolean;
  justification: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Determines if a level requires manual approval based on minimum amount threshold
 */
const isLevelRequired = (
  minimumAmount: number | null | undefined,
  totalAmount: number,
): boolean => {
  if (!minimumAmount || minimumAmount === 0) return true;
  return totalAmount > minimumAmount;
};

/**
 * Determines the status of a level based on its decisions
 */
const getLevelStatus = (
  decisions: PurchaseRequestApprovalDecision[],
): LevelStatus => {
  if (decisions.some((decision) => decision.decision === "rejected")) {
    return "rejected";
  }
  if (decisions.some((decision) => decision.decision === "approved")) {
    return "approved";
  }
  return "pending";
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface ApprovalMetadataProps {
  approval: PurchaseRequestApprovalState;
  currentModuleHistory: PurchaseRequestApprovalModuleHistory | null;
}

function ApprovalMetadata({
  approval,
  currentModuleHistory,
}: ApprovalMetadataProps) {
  const normalizedJustification = currentModuleHistory?.justification ?? "";
  const trimmedJustification = normalizedJustification.trim();
  const hasJustification = trimmedJustification.length > 0;
  const authorName = currentModuleHistory?.justification_author_name;
  const submittedAt = currentModuleHistory?.justification_submitted_at;
  const hasMetadata = Boolean(authorName ?? submittedAt);

  const showPurchaseTotal =
    (approval.invoice_total ?? approval.selections_total_ars) &&
    approval.has_purchase_selections;

  if (!showPurchaseTotal && !hasJustification) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Total de la Compra */}
      {showPurchaseTotal && (
        <Item variant="outline" size="sm">
          <ItemMedia variant="icon">
            {approval.invoice_total ? (
              <Receipt className="size-4" />
            ) : (
              <ShoppingCart className="size-4" />
            )}
          </ItemMedia>
          <ItemContent>
            <ItemTitle>Total de la Compra</ItemTitle>
            <ItemDescription>
              {approval.invoice_total ? (
                <>
                  {formatCurrencyWithSymbol(
                    approval.invoice_total.amount,
                    approval.invoice_total.currency,
                  )}
                  {approval.invoice_total.currency !== "ARS" &&
                    approval.selections_total_ars && (
                      <>
                        {" "}
                        (
                        {formatCurrencyWithSymbol(
                          approval.selections_total_ars,
                          "ARS",
                        )}
                        )
                      </>
                    )}
                  {" · "}Desde factura
                </>
              ) : (
                <>
                  {formatCurrencyWithSymbol(
                    approval.selections_total_ars ?? 0,
                    "ARS",
                  )}
                  {" · "}Desde selecciones de compra
                </>
              )}
            </ItemDescription>
          </ItemContent>
        </Item>
      )}

      {/* Justificación del comprador */}
      {hasJustification && (
        <Item variant="outline" size="sm">
          <ItemMedia variant="icon">
            <MessageSquareText className="size-4" />
          </ItemMedia>
          <ItemContent>
            <ItemTitle>Justificación del comprador</ItemTitle>
            <ItemDescription className="mt-1 line-clamp-none break-all whitespace-pre-line rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
              {normalizedJustification}
            </ItemDescription>
            {hasMetadata && (
              <p className="text-xs text-slate-500 mt-2 flex flex-wrap gap-1">
                {authorName && <span>Por {authorName}</span>}
                {authorName && submittedAt && <span>{"·"}</span>}
                {submittedAt && <span>{formatDateTimeAR(submittedAt)}</span>}
              </p>
            )}
          </ItemContent>
        </Item>
      )}
    </div>
  );
}

interface RejectedRequestBannerProps {
  canStartFlow: boolean;
  starting: boolean;
  approval: PurchaseRequestApprovalState;
  sectorHref: string | null;
  sectorLinkLabel: string;
  missingWorkflow: boolean;
  onStartFlow: () => void;
}

function RejectedRequestBanner({
  canStartFlow,
  starting,
  approval,
  sectorHref,
  sectorLinkLabel,
  missingWorkflow,
  onStartFlow,
}: RejectedRequestBannerProps) {
  return (
    <Item variant="outline" size="sm" className="border-amber-500">
      <ItemMedia variant="icon">
        <AlertTriangle className="size-5 text-amber-600" />
      </ItemMedia>
      <ItemContent>
        <ItemTitle className="text-amber-900">Solicitud Rechazada</ItemTitle>
        <ItemDescription className="text-amber-700">
          Revisá las decisiones en el historial abajo, modificá las selecciones
          según corresponda y volvé a enviar.
        </ItemDescription>
        {!canStartFlow && (
          <div className="mt-3 flex flex-col gap-2">
            {!approval.has_purchase_selections && (
              <div className="flex items-center gap-2 text-xs text-amber-700">
                <AlertTriangle className="h-3 w-3" />
                <span>
                  Debes seleccionar al menos un producto en &quot;Selección de
                  compra&quot;
                </span>
              </div>
            )}
            {!approval.can_request && (
              <div className="flex items-center gap-2 text-xs text-amber-700">
                <AlertTriangle className="h-3 w-3" />
                <span>No tienes permisos para re-enviar el flujo</span>
              </div>
            )}
            {missingWorkflow && (
              <div className="flex flex-col gap-1 text-xs text-amber-700">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-3 w-3" />
                  <span>El circuito del sector debe estar configurado</span>
                </div>
                {sectorHref && (
                  <Link
                    href={sectorHref}
                    className="inline-flex items-center gap-1 font-medium text-amber-800 hover:text-amber-900 ml-5"
                  >
                    {sectorLinkLabel}
                    <ChevronRight className="h-3 w-3" />
                  </Link>
                )}
              </div>
            )}
          </div>
        )}
      </ItemContent>
      <ItemActions>
        <Button
          type="button"
          onClick={onStartFlow}
          disabled={!canStartFlow || starting}
          size="sm"
          className="w-full sm:w-auto whitespace-nowrap"
        >
          {starting && <Loader2 className="h-3 w-3 animate-spin" />}
          Re-enviar a aprobación
        </Button>
      </ItemActions>
    </Item>
  );
}

interface NotStartedStateProps {
  canStartFlow: boolean;
  starting: boolean;
  approval: PurchaseRequestApprovalState;
  sectorHref: string | null;
  sectorLinkLabel: string;
  missingWorkflow: boolean;
  onStartFlow: () => void;
}

function NotStartedState({
  canStartFlow,
  starting,
  approval,
  sectorHref,
  sectorLinkLabel,
  missingWorkflow,
  onStartFlow,
}: NotStartedStateProps) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/30 p-8 text-center">
      <div className="flex items-center justify-center gap-3 text-slate-600">
        <Clock className="h-5 w-5" />
        <span className="text-sm font-medium">
          El flujo de aprobación todavía no fue iniciado
        </span>
      </div>
      <div className="mt-6 flex flex-col items-center gap-4">
        <Button
          type="button"
          onClick={onStartFlow}
          disabled={!canStartFlow || starting}
        >
          {starting && <Loader2 className="h-4 w-4 animate-spin" />}
          Pedir aprobación de compra
        </Button>
        <div className="flex flex-col gap-2">
          {!approval.has_purchase_selections && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <AlertTriangle className="h-4 w-4" />
              <span>
                Selecciona al menos un producto en &quot;Seleccion de
                compra&quot;
              </span>
            </div>
          )}
          {!approval.can_request && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <AlertTriangle className="h-4 w-4" />
              <span>No tienes permisos para iniciar el flujo</span>
            </div>
          )}
          {missingWorkflow && (
            <div className="flex flex-col items-center gap-2 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                <span>
                  Configurá el circuito de este sector antes de pedir aprobación
                </span>
              </div>
              {sectorHref && (
                <Link
                  href={sectorHref}
                  className="inline-flex items-center gap-1 font-medium text-slate-700 hover:text-slate-900"
                >
                  {sectorLinkLabel}
                  <ChevronRight className="h-3 w-3" />
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface ApprovalLevelCardProps {
  level: ApprovalSnapshotLevel;
  index: number;
  status: LevelStatus;
  decisions: PurchaseRequestApprovalDecision[];
  isActive: boolean;
  isFirstLevel: boolean;
  isLastLevel: boolean;
  selectionsTotal: number;
  currentUserId: number;
  isSupervisor: boolean;
  supervisorReadOnly: boolean;
  flowFinalized: boolean;
  approvalStatus: string;
  canDecide: boolean;
  submitting: boolean;
  highestApprovedLevelIndex: number;
  onApprove: (levelIndex: number) => void;
  onReject: (levelIndex: number) => void;
  onOverride: (levelIndex: number) => void;
}

function ApprovalLevelCard({
  level,
  index,
  status,
  decisions,
  isActive,
  isFirstLevel,
  isLastLevel,
  selectionsTotal,
  currentUserId,
  isSupervisor,
  supervisorReadOnly,
  flowFinalized,
  approvalStatus,
  canDecide,
  submitting,
  highestApprovedLevelIndex,
  onApprove,
  onReject,
  onOverride,
}: ApprovalLevelCardProps) {
  // Derived: level implicitly approved by a higher level approval
  const implicitlyApproved =
    index <= highestApprovedLevelIndex && status === "pending";
  const effectiveStatus = implicitlyApproved ? "approved" : status;

  const statusMeta = LEVEL_STATUS_META[effectiveStatus];
  const StatusIcon = statusMeta.icon;
  const levelRequired = isLevelRequired(level.minimum_amount, selectionsTotal);

  // Derived: user assignments for this level
  const assignedUserIds = new Set(
    (level.assignees ?? []).map((assignee: ApprovalSnapshotLevelAssignee) =>
      Number(assignee.user_id ?? 0),
    ),
  );

  // Derived: can this user act on this level
  const userCanAct =
    !flowFinalized &&
    approvalStatus === "pending_approval" &&
    status === "pending" &&
    levelRequired &&
    canDecide &&
    (assignedUserIds.has(currentUserId) || isSupervisor) &&
    index > highestApprovedLevelIndex;

  // Derived: can supervisor override
  const canOverride =
    isSupervisor && !supervisorReadOnly && decisions.length > 0;

  return (
    <li className="relative overflow-hidden lg:flex-1 lg:flex">
      <div
        className={`${isFirstLevel ? "rounded-t-md border-b-0" : ""} ${
          isLastLevel ? "rounded-b-md border-t-0" : ""
        } overflow-hidden border border-slate-200 lg:border-0 lg:flex-1 lg:flex lg:flex-col`}
      >
        <div className="group relative lg:flex-1 lg:flex lg:flex-col">
          <span
            aria-hidden="true"
            className={`absolute top-0 left-0 h-full w-1 lg:top-auto lg:bottom-0 lg:h-1 lg:w-full transition-colors duration-200 ${
              effectiveStatus === "approved"
                ? "bg-green-600"
                : effectiveStatus === "rejected"
                  ? "bg-red-600"
                  : isActive
                    ? "bg-amber-500"
                    : "bg-transparent group-hover:bg-slate-200"
            }`}
          />
          <span
            className={`${
              !isFirstLevel ? "lg:pl-9" : ""
            } flex items-start px-6 py-5 text-sm font-medium lg:flex-1 lg:flex lg:flex-col`}
          >
            <span className="shrink-0">
              <span
                className={`flex size-10 items-center justify-center rounded-full transition-colors duration-200 ${
                  effectiveStatus === "approved"
                    ? "bg-green-600"
                    : effectiveStatus === "rejected"
                      ? "bg-red-600"
                      : isActive
                        ? "border-2 border-amber-500"
                        : "border-2 border-slate-300"
                }`}
              >
                {effectiveStatus === "approved" ||
                effectiveStatus === "rejected" ? (
                  <StatusIcon className="size-6 text-white" />
                ) : (
                  <span
                    className={`${
                      isActive ? "text-amber-600" : "text-slate-500"
                    }`}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>
                )}
              </span>
            </span>
            <span className="mt-0.5 ml-4 flex min-w-0 flex-col flex-1 lg:justify-between">
              <div>
                {/* Header: Nivel + Status inline */}
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className={`text-sm font-medium ${
                      effectiveStatus === "approved"
                        ? "text-green-600"
                        : effectiveStatus === "rejected"
                          ? "text-red-600"
                          : isActive
                            ? "text-amber-600"
                            : "text-slate-500"
                    }`}
                  >
                    Nivel {index + 1}
                  </span>
                  {!(effectiveStatus === "pending" && !levelRequired) && (
                    <span
                      className={`inline-flex items-center gap-1 text-xs ${
                        effectiveStatus === "approved"
                          ? "text-green-600"
                          : effectiveStatus === "rejected"
                            ? "text-red-600"
                            : "text-slate-500"
                      }`}
                    >
                      <StatusIcon className="h-3 w-3" />
                      {statusMeta.label}
                    </span>
                  )}
                </div>

                <div className="space-y-3">
                  {/* Assignees - compact inline */}
                  <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-600">
                    {(level.assignees ?? []).map(
                      (assignee: ApprovalSnapshotLevelAssignee, i) => (
                        <span
                          key={assignee.user_id}
                          className="inline-flex items-center gap-1"
                        >
                          <User className="h-3 w-3 text-slate-400" />
                          {assignee.display_name}
                          {i < (level.assignees?.length ?? 0) - 1 && (
                            <span className="text-slate-300 ml-1">·</span>
                          )}
                        </span>
                      ),
                    )}
                  </div>

                  {/* Threshold info - only show when there's a minimum amount configured */}
                  {level.minimum_amount != null && level.minimum_amount > 0 && (
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <DollarSign className="h-3 w-3" />
                      <span>
                        Mínimo{" "}
                        {formatCurrencyWithSymbol(level.minimum_amount, "ARS")}
                      </span>
                      {!levelRequired && (
                        <span className="text-green-600 font-medium">
                          · Auto-aprobado
                        </span>
                      )}
                    </div>
                  )}

                  {/* Decisions - compact list */}
                  {decisions.length > 0 && (
                    <div className="space-y-2 pt-2 border-t border-slate-100">
                      {decisions.map((decision) => (
                        <div key={decision.id} className="text-xs">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-slate-700">
                              {decision.user.full_name ?? decision.user.email}
                            </span>
                            <span
                              className={`inline-flex items-center gap-1 ${
                                decision.decision === "approved"
                                  ? "text-green-600"
                                  : "text-red-600"
                              }`}
                            >
                              {decision.decision === "approved" ? (
                                <Check className="h-3 w-3" />
                              ) : (
                                <X className="h-3 w-3" />
                              )}
                              {DECISION_LABEL[decision.decision]}
                            </span>
                            <span className="text-slate-400">
                              {formatDateTimeAR(decision.decided_at)}
                            </span>
                          </div>
                          {decision.comment && (
                            <p className="mt-1 text-slate-600 bg-slate-50 rounded px-2 py-1.5 whitespace-pre-line break-all">
                              {decision.comment}
                            </p>
                          )}
                          {decision.override && (
                            <div className="mt-1 flex items-center gap-1 text-amber-600">
                              <Gavel className="h-3 w-3" />
                              <span>
                                Sobrescrito por{" "}
                                {decision.overridden_by?.full_name ??
                                  decision.overridden_by?.email}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-3 border-t border-slate-100 mt-auto">
                {/* Action buttons */}
                {userCanAct && (
                  <ButtonGroup className="w-full">
                    <Button
                      type="button"
                      size="sm"
                      disabled={submitting}
                      onClick={() => onApprove(index)}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white cursor-pointer border-0"
                    >
                      Aprobar
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={submitting}
                      onClick={() => onReject(index)}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white cursor-pointer border-0"
                    >
                      Rechazar
                    </Button>
                  </ButtonGroup>
                )}
                {/* Override button */}
                {canOverride && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onOverride(index)}
                  >
                    <Gavel className="h-4 w-4" />
                    Sobrescribir decisión
                  </Button>
                )}
              </div>
            </span>
          </span>
        </div>

        {!isFirstLevel && (
          <div
            aria-hidden="true"
            className="absolute inset-0 top-0 left-0 hidden w-3 lg:block"
          >
            <svg
              fill="none"
              viewBox="0 0 12 82"
              preserveAspectRatio="none"
              className="size-full text-slate-300"
            >
              <path
                d="M0.5 0V31L10.5 41L0.5 51V82"
                stroke="currentcolor"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          </div>
        )}
      </div>
    </li>
  );
}

interface RejectDialogProps {
  open: boolean;
  comment: string;
  submitting: boolean;
  onOpenChange: (open: boolean) => void;
  onCommentChange: (comment: string) => void;
  onConfirm: () => void;
}

function RejectDialog({
  open,
  comment,
  submitting,
  onOpenChange,
  onCommentChange,
  onConfirm,
}: RejectDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rechazar nivel</DialogTitle>
          <DialogDescription>
            Describe la razón del rechazo para notificar a los responsables
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reject-comment">Comentario</Label>
            <TextareaWithCounter
              id="reject-comment"
              value={comment}
              onChange={(event) => onCommentChange(event.target.value)}
              placeholder="Explica por qué rechazas esta aprobación"
              maxLength={500}
              showCharacterCount={true}
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancelar
            </Button>
          </DialogClose>
          <Button
            type="button"
            disabled={comment.trim().length < 3 || submitting}
            onClick={onConfirm}
          >
            Confirmar rechazo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface OverrideDialogProps {
  open: boolean;
  decision: PurchaseRequestApprovalDecision["decision"];
  comment: string;
  submitting: boolean;
  onOpenChange: (open: boolean) => void;
  onDecisionChange: (
    decision: PurchaseRequestApprovalDecision["decision"],
  ) => void;
  onCommentChange: (comment: string) => void;
  onConfirm: () => void;
}

function OverrideDialog({
  open,
  decision,
  comment,
  submitting,
  onOpenChange,
  onDecisionChange,
  onCommentChange,
  onConfirm,
}: OverrideDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Sobrescribir decisión</DialogTitle>
          <DialogDescription>
            Selecciona la nueva resolución para este nivel e indica el motivo
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Resultado</Label>
            <Select value={decision} onValueChange={onDecisionChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="approved">Aprobado</SelectItem>
                <SelectItem value="rejected">Rechazado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="override-comment">Comentario</Label>
            <TextareaWithCounter
              id="override-comment"
              value={comment}
              onChange={(event) => onCommentChange(event.target.value)}
              placeholder="Explica el motivo del override"
              maxLength={500}
              showCharacterCount={true}
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancelar
            </Button>
          </DialogClose>
          <Button
            type="button"
            disabled={comment.trim().length < 3 || submitting}
            onClick={onConfirm}
          >
            <Gavel className="h-4 w-4" />
            Guardar override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface StartApprovalDialogProps {
  open: boolean;
  justification: string;
  starting: boolean;
  onOpenChange: (open: boolean) => void;
  onJustificationChange: (justification: string) => void;
  onConfirm: () => void;
}

function StartApprovalDialog({
  open,
  justification,
  starting,
  onOpenChange,
  onJustificationChange,
  onConfirm,
}: StartApprovalDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Justificá tu selección</DialogTitle>
          <DialogDescription>
            Contale brevemente al equipo aprobador por qué elegiste estos
            productos y proveedores. Es opcional, pero ayuda a acelerar la
            decisión.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="start-justification">Comentario</Label>
            <TextareaWithCounter
              id="start-justification"
              value={justification}
              onChange={(event) => onJustificationChange(event.target.value)}
              placeholder="Ejemplo: Necesitamos asegurar disponibilidad inmediata y el proveedor A es el único con stock confirmado."
              maxLength={500}
              showCharacterCount={true}
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancelar
            </Button>
          </DialogClose>
          <Button type="button" onClick={onConfirm} disabled={starting}>
            {starting && <Loader2 className="h-4 w-4 animate-spin" />}
            Enviar a aprobación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function PurchaseRequestApprovalPanel({
  approval,
  purchaseRequestId,
  sectorId,
  sectorName,
}: PurchaseRequestApprovalPanelProps) {
  const routes = useOrganizationRoutes();
  const { auth } = usePage<SharedData>().props;
  const currentUser = auth.user;
  const groups = auth.groups ?? [];
  const isSupervisor = groups.includes(GROUPS.supervisor);
  const currentUserId = currentUser.id;

  // ============================================================================
  // DERIVED STATE
  // ============================================================================

  const snapshotLevels = approval.snapshot?.levels ?? [];
  const pendingLevelIndex =
    approval.pending_level_index ?? approval.current_level_index ?? null;
  const highestApprovedLevelIndex = approval.highest_approved_level_index ?? -1;
  const workflowConfigured = approval.workflow_configured ?? false;
  const missingWorkflow = !workflowConfigured;
  const sectorHref = sectorId ? routes.sectors.show(sectorId) : null;
  const sectorLinkLabel = sectorName
    ? `Ir al sector ${sectorName}`
    : "Ir a la configuración del sector";
  const selectionsTotal = approval.selections_total_ars ?? 0;
  const moduleHistory: PurchaseRequestApprovalModuleHistory[] =
    approval.modules_history ?? [];
  const currentModuleHistory: PurchaseRequestApprovalModuleHistory | null =
    moduleHistory.find((entry) => entry.is_current === true) ?? null;

  const flowFinalized = ["approved", "rejected"].includes(approval.status);
  const supervisorReadOnly = isSupervisor && flowFinalized;

  const canStartFlow =
    (!approval.started || approval.status === "rejected") &&
    approval.can_request &&
    approval.has_purchase_selections &&
    workflowConfigured;

  // Group decisions by level index (memoized)
  const decisionsByLevel = useMemo(() => {
    const map = new Map<number, PurchaseRequestApprovalDecision[]>();
    approval.decisions.forEach((decision) => {
      const bucket = map.get(decision.level_index) ?? [];
      bucket.push(decision);
      map.set(decision.level_index, bucket);
    });
    map.forEach((bucket) => {
      bucket.sort((a, b) => (a.decided_at < b.decided_at ? -1 : 1));
    });
    return map;
  }, [approval.decisions]);

  // Compute level statuses (derived from decisions)
  const levelStatuses = useMemo(
    () =>
      snapshotLevels.map((_, index) => {
        const decisions = decisionsByLevel.get(index) ?? [];
        return getLevelStatus(decisions);
      }),
    [snapshotLevels, decisionsByLevel],
  );

  // ============================================================================
  // LOCAL STATE (minimized)
  // ============================================================================

  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rejectDialog, setRejectDialog] = useState<DialogState>({
    open: false,
    levelIndex: null,
    comment: "",
  });
  const [overrideDialog, setOverrideDialog] = useState<OverrideDialogState>({
    open: false,
    levelIndex: null,
    comment: "",
    decision: "approved",
  });
  const [startDialog, setStartDialog] = useState<StartDialogState>({
    open: false,
    justification: "",
  });

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleStart = (justificationText: string) => {
    setStarting(true);
    router.post(
      routes.purchaseRequests.approvalFlow(purchaseRequestId),
      {
        approval_flow: {
          justification: justificationText,
        },
      },
      {
        preserveScroll: true,
        preserveState: false,
        onFinish: () => setStarting(false),
      },
    );
  };

  const openStartDialog = () => {
    if (!canStartFlow || starting) return;
    setStartDialog({ open: true, justification: "" });
  };

  const handleStartDialogChange = (open: boolean) => {
    if (!open) {
      setStartDialog({ open: false, justification: "" });
    } else {
      setStartDialog((prev) => ({ ...prev, open }));
    }
  };

  const submitStartDialog = () => {
    const payload = startDialog.justification.trim();
    setStartDialog({ open: false, justification: "" });
    handleStart(payload);
  };

  const submitDecision = (
    decision: PurchaseRequestApprovalDecision["decision"],
    levelIndex: number,
    comment: string | null,
    override = false,
  ) => {
    setSubmitting(true);
    router.post(
      routes.purchaseRequests.approvals(purchaseRequestId),
      {
        approval: {
          decision,
          level_index: levelIndex,
          comment,
          override,
        },
      },
      {
        preserveScroll: true,
        preserveState: false,
        onFinish: () => setSubmitting(false),
      },
    );
  };

  const handleApprove = (levelIndex: number) => {
    submitDecision("approved", levelIndex, null);
  };

  const handleReject = () => {
    if (rejectDialog.levelIndex == null) return;
    submitDecision("rejected", rejectDialog.levelIndex, rejectDialog.comment);
    setRejectDialog({ open: false, levelIndex: null, comment: "" });
  };

  const handleOverrideSubmit = () => {
    if (overrideDialog.levelIndex == null) return;
    submitDecision(
      overrideDialog.decision,
      overrideDialog.levelIndex,
      overrideDialog.comment,
      true,
    );
    setOverrideDialog({
      open: false,
      levelIndex: null,
      comment: "",
      decision: "approved",
    });
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <>
      <Card
        className={supervisorReadOnly ? "opacity-80" : undefined}
        aria-disabled={supervisorReadOnly}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Estado de aprobaciones</CardTitle>
            {approval.module_number && approval.module_number > 1 && (
              <Badge variant="secondary">
                Aprobación #{approval.module_number}
              </Badge>
            )}
          </div>
          <CardDescription>
            Gestiona el circuito de aprobaciones y revisa la evolución por nivel
            {approval.module_number && approval.module_number > 1 && (
              <span className="mt-1 block text-amber-600">
                Esta solicitud ha pasado por múltiples intentos de aprobación
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Metadata Section */}
          <ApprovalMetadata
            approval={approval}
            currentModuleHistory={currentModuleHistory}
          />

          {/* Rejected Request Banner */}
          {approval.status === "rejected" && (
            <RejectedRequestBanner
              canStartFlow={canStartFlow}
              starting={starting}
              approval={approval}
              sectorHref={sectorHref}
              sectorLinkLabel={sectorLinkLabel}
              missingWorkflow={missingWorkflow}
              onStartFlow={openStartDialog}
            />
          )}

          {/* Not Started State */}
          {!approval.started && approval.status !== "rejected" ? (
            <NotStartedState
              canStartFlow={canStartFlow}
              starting={starting}
              approval={approval}
              sectorHref={sectorHref}
              sectorLinkLabel={sectorLinkLabel}
              missingWorkflow={missingWorkflow}
              onStartFlow={openStartDialog}
            />
          ) : (
            <div className="lg:border-t lg:border-b lg:border-slate-200">
              <nav
                aria-label="Progreso de aprobaciones"
                className="mx-auto max-w-7xl"
              >
                <ol
                  role="list"
                  className="overflow-hidden rounded-md lg:flex lg:rounded-none lg:border-r lg:border-l lg:border-slate-200 lg:items-stretch"
                >
                  {snapshotLevels.map((level, index) => {
                    const status = levelStatuses[index] ?? "pending";
                    const decisions = decisionsByLevel.get(index) ?? [];
                    const isActive =
                      !flowFinalized && pendingLevelIndex === index;

                    return (
                      <ApprovalLevelCard
                        key={index}
                        level={level}
                        index={index}
                        status={status}
                        decisions={decisions}
                        isActive={isActive}
                        isFirstLevel={index === 0}
                        isLastLevel={index === snapshotLevels.length - 1}
                        selectionsTotal={selectionsTotal}
                        currentUserId={currentUserId}
                        isSupervisor={isSupervisor}
                        supervisorReadOnly={supervisorReadOnly}
                        flowFinalized={flowFinalized}
                        approvalStatus={approval.status}
                        canDecide={approval.can_decide}
                        submitting={submitting}
                        highestApprovedLevelIndex={highestApprovedLevelIndex}
                        onApprove={handleApprove}
                        onReject={(levelIndex) =>
                          setRejectDialog({
                            open: true,
                            levelIndex,
                            comment: "",
                          })
                        }
                        onOverride={(levelIndex) =>
                          setOverrideDialog({
                            open: true,
                            levelIndex,
                            comment: "",
                            decision: "approved",
                          })
                        }
                      />
                    );
                  })}
                </ol>
              </nav>

              {snapshotLevels.length === 0 && (
                <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
                  No se pudo recuperar el snapshot del flujo. Inicia nuevamente
                  la aprobación desde el sector si persiste.
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <RejectDialog
        open={rejectDialog.open}
        comment={rejectDialog.comment}
        submitting={submitting}
        onOpenChange={(open) =>
          setRejectDialog((state) => ({ ...state, open }))
        }
        onCommentChange={(comment) =>
          setRejectDialog((state) => ({ ...state, comment }))
        }
        onConfirm={handleReject}
      />

      <OverrideDialog
        open={overrideDialog.open}
        decision={overrideDialog.decision}
        comment={overrideDialog.comment}
        submitting={submitting}
        onOpenChange={(open) =>
          setOverrideDialog((state) => ({ ...state, open }))
        }
        onDecisionChange={(decision) =>
          setOverrideDialog((state) => ({ ...state, decision }))
        }
        onCommentChange={(comment) =>
          setOverrideDialog((state) => ({ ...state, comment }))
        }
        onConfirm={handleOverrideSubmit}
      />

      <StartApprovalDialog
        open={startDialog.open}
        justification={startDialog.justification}
        starting={starting}
        onOpenChange={handleStartDialogChange}
        onJustificationChange={(justification) =>
          setStartDialog((state) => ({ ...state, justification }))
        }
        onConfirm={submitStartDialog}
      />
    </>
  );
}
